/**
 * Paired-device credential + cached device context.
 *
 * A floor device is enrolled ONCE by an admin: in web-admin's Devices screen
 * the admin mints an enrollment/device token (shown a single time), then pastes
 * it into this device's pairing screen. We persist it in localStorage and
 * inject it on every API call as the `x-warehouselogix-device` header (see
 * net/api.ts). It is NOT an Entra bearer token — this app never talks to
 * MSAL/Entra.
 *
 * Physical control of the device is the security perimeter; the token can be
 * rotated/revoked from web-admin, which surfaces here as a 401 that clears the
 * token and drops the device back to the pairing screen.
 */

import { DEVICE_TOKEN_HEADER } from '@warehouselogix/shared';

const TOKEN_KEY = 'warehouselogix.floor.deviceToken';
const CONTEXT_KEY = 'warehouselogix.floor.context';

/** HTTP header carrying the device token (single canonical constant in shared). */
export const DEVICE_HEADER = DEVICE_TOKEN_HEADER;

/** Cached identity of the warehouse site this device is bound to. */
export interface DeviceContext {
  siteId: string;
  siteName: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** Subscribe to token changes (pair / clear). Returns an unsubscribe fn. */
export function subscribeDeviceToken(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getDeviceToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setDeviceToken(token: string): void {
  window.localStorage.setItem(TOKEN_KEY, token.trim());
  emit();
}

/** Clear the token (e.g. on 401/revoke) and return the device to pairing. */
export function clearDeviceToken(): void {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(CONTEXT_KEY);
  emit();
}

export function getDeviceContext(): DeviceContext | null {
  try {
    const raw = window.localStorage.getItem(CONTEXT_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<DeviceContext>;
    if (typeof parsed.siteId === 'string' && typeof parsed.siteName === 'string') {
      return { siteId: parsed.siteId, siteName: parsed.siteName };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Cache the site identity learned from the device-verify call. Lets the hub
 * banner render the site without a server round-trip.
 */
export function setDeviceContext(context: DeviceContext): void {
  window.localStorage.setItem(CONTEXT_KEY, JSON.stringify(context));
}
